import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router';
import { useState } from 'react';
import { api, type CaseDetail, type TransitionEvaluation } from '../api/client.js';
import { DocumentPanel } from '../components/DocumentPanel.js';
import { CallQaPanel } from '../components/CallQaPanel.js';
import { BillingPanel } from '../components/BillingPanel.js';
import { CallCard } from '../components/CallCard.js';
import { CcoButton } from '../components/CcoButton.js';
import { AssignPanel } from '../components/AssignPanel.js';
import { useAuth } from '../auth/useAuth.js';

/**
 * Case detail.
 *
 * The point of this screen is the transition buttons. Every possible action is
 * SHOWN, with blocked ones disabled and stating exactly what is missing —
 * rather than hidden, or offered and then rejected.
 *
 * That is the difference from the system being replaced: a clinician learns
 * "bed letter missing, ePRF not signed" before clicking, once, instead of
 * discovering them one failed attempt at a time.
 */

function TransitionButton({
  t,
  onRun,
  busy,
}: {
  t: TransitionEvaluation;
  onRun: (key: string, reason?: string) => void;
  busy: boolean;
}) {
  const [reason, setReason] = useState('');
  const [asking, setAsking] = useState(false);

  if (asking && t.requiresReason) {
    return (
      <div className="rounded-lg border border-line bg-surface-3 p-3">
        <label className="text-xs text-slate-400">Reason for “{t.label}”</label>
        <textarea
          autoFocus
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={2}
          className="mt-1 w-full rounded border border-line bg-surface-2 p-2 text-sm text-slate-200"
        />
        <div className="mt-2 flex gap-2">
          <button
            disabled={!reason.trim() || busy}
            onClick={() => onRun(t.transitionKey, reason)}
            className="rounded bg-sky-600 px-3 py-1 text-sm font-medium text-white disabled:opacity-40"
          >
            Confirm
          </button>
          <button
            onClick={() => setAsking(false)}
            className="rounded px-3 py-1 text-sm text-slate-400 hover:text-slate-200"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <button
        disabled={!t.allowed || busy}
        onClick={() => (t.requiresReason ? setAsking(true) : onRun(t.transitionKey))}
        className={`w-full rounded-lg px-3 py-2 text-left text-sm font-medium transition ${
          t.allowed
            ? t.viaOverride
              ? 'bg-amber-600/20 text-amber-200 ring-1 ring-amber-500/40 hover:bg-amber-600/30'
              : 'bg-sky-600/20 text-sky-200 ring-1 ring-sky-500/40 hover:bg-sky-600/30'
            : 'cursor-not-allowed bg-slate-800/40 text-slate-400 ring-1 ring-slate-700'
        }`}
      >
        {t.label}
        {/* The override warning appears BEFORE the click, not after. Someone
            approving without the clinical role should know it will be recorded
            while they still have the option not to. */}
        {t.viaOverride && t.allowed && (
          <span className="ml-2 rounded bg-amber-500/20 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-amber-300">
            override — will be recorded
          </span>
        )}
      </button>

      {t.violations.length > 0 && (
        <ul className="mt-1.5 space-y-1 pl-1">
          {t.violations.map((v, i) => (
            <li key={i} className="flex gap-1.5 text-xs text-slate-400">
              <span className="text-slate-500">•</span>
              <span>{v.message}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function PipelinePanel({
  caseId,
  pipeline,
  name,
}: {
  caseId: string;
  pipeline: string;
  name: string;
}) {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const { data } = useQuery({
    queryKey: ['transitions', caseId, pipeline],
    queryFn: () =>
      api.get<{ transitions: TransitionEvaluation[] }>(
        `/cases/${caseId}/pipelines/${pipeline}/transitions`,
      ),
  });

  const mutation = useMutation({
    mutationFn: ({ key, reason }: { key: string; reason?: string }) =>
      api.post(`/cases/${caseId}/pipelines/${pipeline}/transitions/${key}`, { reason }),
    onSuccess: async () => {
      setError(null);
      // Everything on this screen can change: the stage, the timeline, and what
      // the OTHER pipelines will now allow (billing unblocks when QC approves).
      await qc.invalidateQueries({ queryKey: ['case', caseId] });
      await qc.invalidateQueries({ queryKey: ['transitions', caseId] });
      await qc.invalidateQueries({ queryKey: ['cases'] });
    },
    onError: (e: Error) => setError(e.message),
  });

  const transitions = data?.transitions ?? [];
  if (transitions.length === 0) return null;

  return (
    <div className="rounded-xl border border-line bg-surface-2 p-4">
      <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-400">{name}</h3>
      <div className="space-y-3">
        {transitions.map((t) => (
          <TransitionButton
            key={t.transitionKey}
            t={t}
            busy={mutation.isPending}
            onRun={(key, reason) => mutation.mutate({ key, reason })}
          />
        ))}
      </div>
      {error && <p className="mt-2 text-xs text-red-400">{error}</p>}
    </div>
  );
}

export function CasePage() {
  const { id = '' } = useParams();
  const { can } = useAuth();
  // refetch after an autosave, so the transition buttons re-evaluate their
  // guards as fields get filled in — "blocked: no location" clearing itself the
  // moment a location is typed is the thing QContact cannot do.
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['case', id],
    queryFn: () => api.get<CaseDetail>(`/cases/${id}`),
  });

  if (isLoading) return <p className="p-6 text-sm text-slate-400">Loading…</p>;
  if (error) return <p className="p-6 text-sm text-red-400">{error.message}</p>;
  if (!data) return null;

  const c = data.case;

  return (
    <div className="p-6">
      <Link to="/" className="text-xs text-slate-400 hover:text-slate-300">
        ← Dispatch
      </Link>

      <div className="mt-2 flex flex-wrap items-baseline gap-3">
        <h1 className="font-mono text-xl font-semibold text-slate-100">{c.case_number}</h1>
        <span className="text-sm text-slate-400">{String(c.case_type)}</span>
        <span className="rounded bg-slate-700/50 px-2 py-0.5 text-xs text-slate-300">
          {String(c.status)}
        </span>
      </div>

      <div className="mt-5 grid gap-5 lg:grid-cols-[2fr_1fr]">
        <div className="space-y-5">
          {/* Current position in every pipeline, side by side — the thing a
              single-status field cannot express. */}
          <div className="rounded-xl border border-line bg-surface-2 p-4">
            <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-400">
              Pipelines
            </h3>
            <div className="grid gap-3 sm:grid-cols-3">
              {data.pipelines.map((p) => (
                <div key={p.pipeline} className="rounded-lg bg-surface-3 p-3">
                  <p className="text-[11px] text-slate-400">{p.pipeline_name}</p>
                  <p className="mt-1 flex items-center gap-1.5 text-sm text-slate-200">
                    <span
                      className="h-2 w-2 shrink-0 rounded-full"
                      style={{ background: p.colour ?? '#64748b' }}
                    />
                    {p.stage_name}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Always shown, including on a brand new case — an empty panel that
              can be acted on is the point. The previous version rendered only
              when something was already assigned, so a new call offered no way
              to allocate anyone. */}
          <div className="rounded-xl border border-line bg-surface-2 p-4">
            <AssignPanel caseId={id} assigned={data.assignments} />
          </div>

          {/* The intake form. Only EMS road cases have a call card schema so
              far; other case types get theirs with their phase. */}
          {c.case_type === 'ems_road' && (
            <div className="rounded-xl border border-line bg-surface-2 p-4">
              <CallCard
                caseId={id}
                initialCase={c}
                initialDetail={data.detail}
                initialOnScenePhone={
                  data.parties.find((p) => p.party_type === 'on_scene_contact')?.phone_raw ?? ''
                }
                onSaved={() => void refetch()}
              />
            </div>
          )}

          <div className="rounded-xl border border-line bg-surface-2 p-4">
            <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-400">
              Crew message
            </h3>
            <CcoButton caseId={id} />
          </div>

          <CallQaPanel caseId={id} />

          {/* Only for people who do billing. A dispatcher does not need a
              debtors form on an emergency case. */}
          {can('billing.read') && <BillingPanel caseId={id} />}

          <DocumentPanel caseId={id} />

          <div className="rounded-xl border border-line bg-surface-2 p-4">
            <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-400">
              Timeline
            </h3>
            <ul className="space-y-2">
              {data.timeline.map((t, i) => (
                <li key={i} className="flex gap-3 text-sm">
                  <span className="w-32 shrink-0 tabular-nums text-xs text-slate-500">
                    {new Date(t.occurred_at).toLocaleString('en-ZA', {
                      dateStyle: 'short',
                      timeStyle: 'short',
                    })}
                  </span>
                  <span className="text-slate-300">{t.event_type.replace(/_/g, ' ')}</span>
                  {t.data?.['via_override'] === true && (
                    <span className="rounded bg-amber-500/20 px-1.5 text-[10px] uppercase text-amber-300">
                      override
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="space-y-4">
          {data.pipelines.map((p) => (
            <PipelinePanel
              key={p.pipeline}
              caseId={id}
              pipeline={p.pipeline}
              name={p.pipeline_name}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
